<template>
  <node-view-wrapper class="relative inline">
    <span
      ref="chipRef"
      :class="[
        'conditional-chip -mt-[1px] cursor-pointer whitespace-normal rounded align-baseline shadow-sm',
        isConditionOnly
          ? 'border border-dashed border-amber-400/50 bg-amber-400/15 dark:border-amber-500/40 dark:bg-amber-700/30'
          : 'bg-blue-400/10 dark:bg-blue-800/40',
        hasBlock ? 'conditional-chip-block mb-0 mt-3 block w-full px-1 py-0.5' : 'inline px-1 py-0.5'
      ]"
      contenteditable="false"
      data-drag-handle
      @click="editConditional"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip">
      <span
        :class="['align-middle', isConditionOnly ? 'text-sm italic text-amber-700 dark:text-amber-400/90' : '']"
        v-html="getDisplayText()" />
    </span>

    <!-- Teleport tooltip to body to avoid overflow issues -->
    <Teleport to="body">
      <div
        v-if="isTooltipVisible"
        ref="tooltipRef"
        class="bg-surface-50 fixed z-[9999] w-[560px] max-h-[calc(100vh-1.5rem)] max-w-[calc(100vw-1.5rem)] overflow-auto rounded-md border border-gray-200 p-4 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-700"
        :style="tooltipStyle"
        @mouseenter="cancelHideTooltip"
        @mouseleave="hideTooltip">
        <div v-if="branches.length" class="mb-2 space-y-2">
          <div v-for="(branch, index) in branches" :key="index" class="p-3">
            <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Branch {{ index + 1 }}
              <template v-if="branch.rules.length > 1">
                · {{ branch.match === 'any' ? 'any rule may match' : 'all rules must match' }}
              </template>
            </div>
            <div class="space-y-1 text-xs text-gray-500 dark:text-gray-400">
              <div v-for="(rule, ruleIndex) in branch.rules" :key="ruleIndex" class="flex items-center">
                <span class="font-semibold">{{ displayConditionRuleField(rule) }}</span>
                <span class="mx-1">{{ formatOperator(rule.operator) }}</span>
                <span class="italic">{{ rule.value }}</span>
              </div>
            </div>
            <div class="mt-3 text-gray-800 dark:text-gray-200" v-html="branch.content" />
          </div>
        </div>
        <div v-if="node.attrs.elseHtml || node.attrs.elseContent" class="rounded bg-gray-500/5 p-3">
          <div class="mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">Default / Fallback</div>
          <div class="text-gray-800 dark:text-gray-200" v-html="node.attrs.elseHtml || node.attrs.elseContent" />
        </div>
      </div>
    </Teleport>
  </node-view-wrapper>
</template>

<script setup>
import { ref, computed, nextTick, onUnmounted, watch } from 'vue'
import { NodeViewWrapper } from '@tiptap/vue-3'
import { displayConditionRuleField, migrateLegacyConditionsToBranches } from '../liquidConditionals'

const props = defineProps({
  editor: {
    type: Object,
    required: true
  },
  node: {
    type: Object,
    required: true
  },
  updateAttributes: {
    type: Function,
    required: true
  },
  deleteNode: {
    type: Function,
    required: true
  }
})

const chipRef = ref(null)
const tooltipRef = ref(null)
const isTooltipVisible = ref(false)
const tooltipPosition = ref({ left: 0, top: 0 })
const TOOLTIP_OFFSET = 8
const VIEWPORT_GUTTER = 12
let hideTimer = null

const branches = computed(() => {
  if (Array.isArray(props.node.attrs.branches) && props.node.attrs.branches.length) {
    return props.node.attrs.branches
  }
  return migrateLegacyConditionsToBranches(props.node.attrs.conditions || [])
})

const tooltipStyle = computed(() => {
  const { left, top } = tooltipPosition.value
  return {
    left: `${left}px`,
    top: `${top}px`
  }
})

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max)
}

const positionTooltip = () => {
  if (!chipRef.value || !tooltipRef.value) return

  const chipRect = chipRef.value.getBoundingClientRect()
  const tooltipWidth = tooltipRef.value.offsetWidth
  const tooltipHeight = tooltipRef.value.offsetHeight
  const maxLeft = Math.max(VIEWPORT_GUTTER, window.innerWidth - tooltipWidth - VIEWPORT_GUTTER)
  const preferredLeft = chipRect.left + chipRect.width / 2 - tooltipWidth / 2
  const left = clamp(preferredLeft, VIEWPORT_GUTTER, maxLeft)

  const preferredTop = chipRect.bottom + TOOLTIP_OFFSET
  const topIfAbove = chipRect.top - tooltipHeight - TOOLTIP_OFFSET
  const fitsBelow = preferredTop + tooltipHeight <= window.innerHeight - VIEWPORT_GUTTER
  const fitsAbove = topIfAbove >= VIEWPORT_GUTTER
  const rawTop = fitsBelow || !fitsAbove ? preferredTop : topIfAbove
  const maxTop = Math.max(VIEWPORT_GUTTER, window.innerHeight - tooltipHeight - VIEWPORT_GUTTER)
  const top = clamp(rawTop, VIEWPORT_GUTTER, maxTop)

  tooltipPosition.value = { left, top }
}

const showTooltip = async () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }

  if (!chipRef.value) return

  isTooltipVisible.value = true
  await nextTick()
  positionTooltip()
}

const hideTooltip = () => {
  hideTimer = setTimeout(() => {
    isTooltipVisible.value = false
  }, 100)
}

const cancelHideTooltip = () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}


watch(isTooltipVisible, (visible) => {
  if (visible) {
    window.addEventListener('resize', positionTooltip)
    window.addEventListener('scroll', positionTooltip, true)
    return
  }

  window.removeEventListener('resize', positionTooltip)
  window.removeEventListener('scroll', positionTooltip, true)
})
onUnmounted(() => {
  if (hideTimer) {
    clearTimeout(hideTimer)
  }
  window.removeEventListener('resize', positionTooltip)
  window.removeEventListener('scroll', positionTooltip, true)
})

const blockTagRegex = /<(p|h1|h2|h3|h4|h5|h6|ul|ol|li|div|blockquote|pre|table|tr|td|th)\b/i
const hasBlock = computed(() => {
  const node = props.node
  if (!node?.attrs) return false
  if (node.attrs.elseHtml && blockTagRegex.test(String(node.attrs.elseHtml))) return true
  return branches.value.some((branch) => branch?.content && blockTagRegex.test(String(branch.content)))
})

// True when there's no else/fallback content - shows condition expression instead of content preview
const isConditionOnly = computed(() => {
  return !props.node.attrs.elseHtml && !props.node.attrs.elseContent
})

const formatOperator = (operator) => {
  if (operator === 'contains') return 'contains'
  return operator || '='
}

const summarizeRule = (rule) => {
  return `${displayConditionRuleField(rule)} ${formatOperator(rule.operator)} ${rule.value}`
}

const getDisplayText = () => {
  // Priority 1: Show fallback text if it exists (this IS the default content)
  if (props.node.attrs.elseHtml) {
    return props.node.attrs.elseHtml || ''
  }
  if (props.node.attrs.elseContent) {
    return String(props.node.attrs.elseContent)
  }

  const activeBranches = branches.value
  if (activeBranches.length > 0) {
    const firstBranch = activeBranches[0]
    const joiner = firstBranch.match === 'any' ? ' OR ' : ' AND '
    const summary = firstBranch.rules.map(summarizeRule).join(joiner)
    if (activeBranches.length === 1) return `Only when ${summary}`
    return `Conditional: ${summary}… + ${activeBranches.length - 1} more`
  }

  return 'Conditional'
}

const editConditional = () => {
  props.editor.emit('editConditional', {
    conditionalId: props.node.attrs.conditionalId,
    branches: branches.value,
    conditions: props.node.attrs.conditions,
    elseContent: props.node.attrs.elseHtml || props.node.attrs.elseContent,
    elseHtml: props.node.attrs.elseHtml || props.node.attrs.elseContent
  })
}
</script>
